import { useEffect, useState } from 'react';
import { faltaNoServico, type Subarea } from '../../utils/areasEmpresa';
import { CamposAreaServico } from '../comercial-propostas/CamposAreaServico';
import { criarServico, erroDe, listarAreas, salvarSubarea, type EscolhaArea } from '../comercial-propostas/apiCatalogo';
import { inputStyle, labelStyle } from './utils';

// Modal pra cadastrar serviço novo (chamado de dentro do ModalSelecionarServico).
//
// 04/10/2026 — o serviço nasce com ÁREA da empresa (uma das 6; não existe mais
// "Outros"), sub-área quando a área tem, e código de serviço da NF. Os três
// campos são os mesmos do catálogo (`CamposAreaServico`).
export default function ModalCadastrarServico({ nomeInicial, onClose, onCriado }: {
  nomeInicial?: string;
  onClose: () => void;
  onCriado: (novo: any) => void;
}) {
  const [nome, setNome] = useState(nomeInicial || '');
  const [unidade, setUnidade] = useState('vb');
  const [codigo, setCodigo] = useState('');
  const [escolha, setEscolha] = useState<EscolhaArea>({ setor_chave: null, subarea_id: null, cnae: null });
  const [subareas, setSubareas] = useState<Subarea[]>([]);
  const [estadoAreas, setEstadoAreas] = useState<'carregando' | 'ok' | 'erro'>('carregando');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function carregarAreas() {
    setEstadoAreas('carregando');
    try {
      const areas = await listarAreas();
      setSubareas(areas.flatMap(a => a.subareas));
      setEstadoAreas('ok');
    } catch { setEstadoAreas('erro'); }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregarAreas(); }, []);

  async function criarSubarea(setor: string, nomeSub: string): Promise<Subarea> {
    try {
      const nova = await salvarSubarea({ setor_chave: setor, nome: nomeSub });
      setSubareas(s => [...s, nova]);
      return nova;
    } catch (e) { throw new Error(erroDe(e, 'Não consegui criar a sub-área.')); }
  }

  // Sem a lista de sub-áreas não dá para saber se a área exige uma: não salva.
  const falta = estadoAreas !== 'ok'
    ? (estadoAreas === 'erro' ? 'Não consegui carregar as áreas.' : 'Carregando as áreas…')
    : faltaNoServico({ nome, ...escolha }, subareas);

  async function salvar() {
    if (falta) { setErro(falta); return; }
    setSalvando(true); setErro(null);
    try {
      const novo = await criarServico({
        nome: nome.trim(), unidade: unidade.trim() || 'vb', codigo: codigo.trim() || null, ...escolha,
      });
      onCriado(novo);
    } catch (e) {
      setErro(erroDe(e, 'Não consegui cadastrar o serviço. Tente de novo.'));
    } finally { setSalvando(false); }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000,
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 10, padding: 22, width: '94%', maxWidth: 520, maxHeight: '94vh', overflowY: 'auto' }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 14, color: '#020D7F' }}>+ Cadastrar novo serviço</h3>

        <label style={labelStyle} htmlFor="novo-servico-nome">Nome *</label>
        <input id="novo-servico-nome" value={nome} onChange={e => setNome(e.target.value)} autoFocus
          style={inputStyle} />

        <div style={{ ...labelStyle, marginTop: 10 }}>Área * · Sub-área · Código de serviço da NF (CNAE) *</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <CamposAreaServico valor={escolha} onChange={v => { setEscolha(v); setErro(null); }} subareas={subareas}
            onCriarSubarea={criarSubarea} popupNoPai desabilitado={estadoAreas !== 'ok'} />
        </div>
        {estadoAreas === 'erro' && (
          <div style={{ fontSize: 12, color: '#B91626', marginTop: 6 }}>
            Não consegui carregar as áreas.{' '}
            <button type="button" onClick={carregarAreas}
              style={{ border: 'none', background: 'none', color: '#0930D0', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
              Tentar de novo
            </button>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
          <div>
            <label style={labelStyle} htmlFor="novo-servico-unidade">Unidade</label>
            <input id="novo-servico-unidade" value={unidade} onChange={e => setUnidade(e.target.value)}
              placeholder="Ex.: vb, h, dia, km, m, m²" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle} htmlFor="novo-servico-codigo">Código (opcional)</label>
            <input id="novo-servico-codigo" value={codigo} onChange={e => setCodigo(e.target.value)}
              placeholder="Em branco, o sistema gera" style={inputStyle} />
          </div>
        </div>

        {erro && <div style={{ fontSize: 12, color: '#B91626', marginTop: 12 }}>{erro}</div>}
        {/* O botão fica apagado enquanto falta algo — o motivo tem que estar escrito
            (o `title` do botão não existe no celular). */}
        {!erro && falta && estadoAreas === 'ok' && (
          <div style={{ fontSize: 12, color: '#B07500', marginTop: 12 }}>{falta}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button onClick={onClose}
            style={{ padding: '8px 16px', fontSize: 12, border: '0.5px solid #ddd',
              borderRadius: 5, background: '#fff', cursor: 'pointer' }}>Cancelar</button>
          <button onClick={salvar} disabled={salvando || !!falta} title={falta || undefined}
            style={{ padding: '8px 18px', fontSize: 12, fontWeight: 600, border: 'none',
              borderRadius: 5, background: '#0930D0', color: '#fff',
              cursor: (salvando || falta) ? 'not-allowed' : 'pointer', opacity: (salvando || falta) ? 0.5 : 1 }}>
            {salvando ? 'Salvando…' : 'Cadastrar e selecionar'}
          </button>
        </div>
      </div>
    </div>
  );
}
